import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Tag, statusTone, Loader, Empty } from "@/components/kit";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Briefcase, MapPin, Calendar, Plus, CheckCircle2, Zap, Clock, IndianRupee,
} from "lucide-react";

export default function KridaWork() {
  const { user } = useAuth();
  const isOrg = user?.role === "organisation";
  const [opps, setOpps] = useState(null);
  const [catalog, setCatalog] = useState({ sports: [], config: {} });
  const [filters, setFilters] = useState({ sport: "all", role: "all" });

  const load = () => {
    const params = {};
    if (filters.sport !== "all") params.sport = filters.sport;
    if (filters.role !== "all") params.role = filters.role;
    api.get("/opportunities", { params }).then((r) => setOpps(r.data.opportunities)).catch(() => setOpps(false));
  };
  useEffect(() => { load(); }, [filters]);
  useEffect(() => { api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => {}); }, []);

  const apply = async (id) => {
    try { await api.post(`/opportunities/${id}/apply`, { note: "" }); toast.success("Application submitted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not apply"); }
  };

  return (
    <div className="fade-up">
      <PageHeader overline="Professional opportunity engine" title="Krida Work"
        subtitle={isOrg ? "Post and manage workforce opportunities." : "Opportunities matched to your profile with explainable scoring."}
        actions={isOrg && <CreateOpportunity catalog={catalog} onCreated={load} />}
        testid="krida-work-page" />

      <div className="flex flex-wrap gap-3 mb-6">
        <FilterSelect label="Sport" value={filters.sport} onChange={(v) => setFilters((f) => ({ ...f, sport: v }))}
          options={["all", ...catalog.sports.map((s) => s.name)]} testid="work-filter-sport" />
        <FilterSelect label="Role" value={filters.role} onChange={(v) => setFilters((f) => ({ ...f, role: v }))}
          options={["all", ...(catalog.config?.professional_roles || [])]} testid="work-filter-role" />
      </div>

      {opps === null ? <Loader /> : opps.length === 0 ? <Empty label="No opportunities found" /> : (
        <div className="grid md:grid-cols-2 gap-4">
          {opps.map((o) => (
            <div key={o.id} className="krida-card p-5" data-testid={`work-opp-${o.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-heading text-xl font-bold">{o.title}</div>
                  <div className="text-sm text-slate-500">{o.org_name}</div>
                </div>
                {!isOrg && o.match && (
                  <div className="text-center flex-shrink-0">
                    <div className="font-mono text-2xl font-bold text-krida-emerald">{o.match.score}%</div>
                    <div className="overline text-[8px] text-slate-500">Match</div>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <Tag tone="cobalt">{o.opportunity_type}</Tag>
                <Tag>{o.level}</Tag>
                <Tag tone="amber">₹{o.compensation.amount.toLocaleString("en-IN")} {o.compensation.type}</Tag>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-4 text-xs text-slate-400">
                <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {o.location.city}</span>
                <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {new Date(o.date).toLocaleDateString()}</span>
                <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {o.start_time}–{o.end_time}</span>
                <span className="flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" /> {o.num_required} needed</span>
              </div>
              {!isOrg && o.match?.reasons?.length > 0 && (
                <div className="mt-4 p-3 rounded-xl bg-krida-emerald/5 border border-krida-emerald/15">
                  <div className="overline text-krida-emerald mb-1.5 flex items-center gap-1"><Zap className="w-3 h-3" /> Why you match</div>
                  <div className="space-y-1">
                    {o.match.reasons.slice(0, 3).map((r, i) => (
                      <div key={i} className="text-xs text-slate-300 flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-krida-emerald" /> {r}</div>
                    ))}
                  </div>
                </div>
              )}
              {!isOrg && (
                <button onClick={() => apply(o.id)} data-testid={`apply-btn-${o.id}`}
                  className="mt-4 w-full py-2.5 rounded-xl bg-krida-crimson hover:bg-krida-crimsonDark font-semibold text-sm transition-colors">
                  Apply now
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="w-44 bg-black/40 border-white/10 rounded-xl"><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent className="bg-krida-card border-white/10 text-slate-100 max-h-72">
        {options.map((o) => <SelectItem key={o} value={o}>{o === "all" ? `All ${label}s` : o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function CreateOpportunity({ catalog, onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", sport: "Basketball", role: "Referee", level: "State",
    opportunity_type: "Match assignment", city: "Chennai", state: "Tamil Nadu", venue: "",
    date: "", num_required: 2, compensation_amount: 2500, compensation_type: "Match fee" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.title || !f.date) { toast.error("Title and date are required"); return; }
    try { await api.post("/opportunities", f); toast.success("Opportunity published"); setOpen(false); onCreated(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="create-opportunity-btn" className="px-4 py-2.5 rounded-xl bg-krida-crimson hover:bg-krida-crimsonDark font-semibold text-sm flex items-center gap-2 transition-colors"><Plus className="w-4 h-4" /> Post opportunity</button>
      </DialogTrigger>
      <DialogContent className="bg-krida-card border-white/10 text-slate-100 max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Create opportunity</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <Inp label="Title" value={f.title} onChange={(v) => set("title", v)} testid="opp-title" />
          <div className="grid grid-cols-2 gap-3">
            <Sel label="Sport" value={f.sport} onChange={(v) => set("sport", v)} options={catalog.sports.map((s) => s.name)} testid="opp-sport" />
            <Sel label="Role" value={f.role} onChange={(v) => set("role", v)} options={catalog.config?.professional_roles || []} testid="opp-role" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Inp label="City" value={f.city} onChange={(v) => set("city", v)} />
            <Inp label="Venue" value={f.venue} onChange={(v) => set("venue", v)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Inp label="Date" type="date" value={f.date} onChange={(v) => set("date", v)} testid="opp-date" />
            <Inp label="Number required" type="number" value={f.num_required} onChange={(v) => set("num_required", Number(v))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Inp label="Compensation (₹)" type="number" value={f.compensation_amount} onChange={(v) => set("compensation_amount", Number(v))} />
            <Sel label="Type" value={f.opportunity_type} onChange={(v) => set("opportunity_type", v)}
              options={["Match assignment", "Tournament assignment", "Part-time", "Contract", "Event assignment", "Volunteer"]} />
          </div>
          <button onClick={submit} data-testid="opp-submit" className="w-full py-3 rounded-xl bg-krida-crimson hover:bg-krida-crimsonDark font-semibold transition-colors">Publish opportunity</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Inp({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="overline text-slate-500 block mb-1">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 focus:border-krida-crimson/60 outline-none text-sm" />
    </div>
  );
}
function Sel({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="overline text-slate-500 block mb-1">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="bg-black/40 border-white/10 rounded-xl text-sm"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-krida-card border-white/10 text-slate-100 max-h-64">{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
